import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosPrivate from 'config/axiosPrivate';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { useDispatch, useSelector } from 'react-redux';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import { UserSessionSelector } from 'store/UserSession';
import { DownloadTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { exportTextsByIdsAsZip } from 'components/shared/ExcelExport/ExcelExportButton';
import DownloadModal from 'components/shared/modal/DownloadModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { usePagination } from 'utils/hooks/usePagination';
import DataTable from 'components/shared/DataTable';
import TablePagination from 'components/shared/Pagination/TablePagination';
import { useTextStatusChange, isTextRevisionNotAllowedError } from 'utils/hooks/useTextStatusChange';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import LinkIcon from 'icons/LinkIcon';
import TranslationPopin from 'components/shared/SmartcontentModal/TranslationPopin';
import './TraductionIaList.css';
import { formatDateShort } from 'utils/DateUtils';
import usePageTracking from 'utils/hooks/usePageTracking';

const TRANSLATION_POPIN_CONTEXT_KEY = 'TranslationSmartContentPopinContext';
const DEFAULT_LLM_OPTIONS = [
  { id: 'gpt', name: 'GPT' },
  { id: 'claude', name: 'Claude' },
  { id: 'claude_gpt', name: 'Claude + GPT' },
  { id: 'perplexity', name: 'Perplexity' },
];

const normalizeLlmId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'both' || normalized === 'claude+gpt' || normalized === 'gpt+claude') {
    return 'claude_gpt';
  }
  return normalized.replace(/\s+/g, '_');
};

const formatModelName = (id) => {
  if (id === 'gpt') return 'GPT';
  if (id === 'claude') return 'Claude';
  if (id === 'claude_gpt') return 'Claude + GPT';
  if (id === 'perplexity') return 'Perplexity';
  return String(id || '').toUpperCase();
};

const formatQuotaDisplay = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }
  return Number(value).toLocaleString('fr-FR');
};

const TraductionIaList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isConsultantRoute = history.location.pathname.startsWith('/Consultant');
  const translationIaListPath = isConsultantRoute
    ? '/Consultant/TraductionIA'
    : '/AdminClient/TraductionIA';
  const createTranslationIaPath = isConsultantRoute
    ? '/Consultant/CreateTraductionIA'
    : '/AdminClient/CreateTraductionIA';
  usePageTracking(translationIaListPath);
  const dispatch = useDispatch();
  const [texts, setTexts] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [websiteId, setWebsiteId] = useState(null);
  const [textsPerPage, setTextsPerPage] = useState(10);
  const [openActionsId, setOpenActionsId] = useState(null);
  const [openStatusId, setOpenStatusId] = useState(null);
  const [actionsMenuCoords, setActionsMenuCoords] = useState({ top: 0, left: 0 });
  const [statusMenuCoords, setStatusMenuCoords] = useState({ top: 0, left: 0 });
  const [isTranslationPopinOpen, setIsTranslationPopinOpen] = useState(false);
  const [translationAgents, setTranslationAgents] = useState([]);
  const [availableAiModels, setAvailableAiModels] = useState(DEFAULT_LLM_OPTIONS);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [remainingTexts, setRemainingTexts] = useState(null);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [textPendingDelete, setTextPendingDelete] = useState(null);
  const [isExportingTexts, setIsExportingTexts] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportErrorOpen, setExportErrorOpen] = useState(false);
  const [exportErrorMessage, setExportErrorMessage] = useState('');
  const [isRevisionErrorModalOpen, setIsRevisionErrorModalOpen] = useState(false);
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const { clients, isLoadingClients = false } = useSelector(adminClientClientListSelector) || {};
  const clientsList = Array.isArray(clients) ? clients : [];
  const hasNoWebsiteConfigured =
    clients !== null && !isLoadingClients && clientsList.length === 0;
  const { user: currentUser } = useSelector(UserSessionSelector) || {};
  const currentWebsiteId = useMemo(() => {
    const selected = clientsList.find((w) => (w.urlSite || w.url || w.name) === selectedWebsiteUrl);
    return selected?.id || null;
  }, [clientsList, selectedWebsiteUrl]);

  useEffect(() => {
    if (clients === null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  useEffect(() => {
    document.title = "Typix - Mes traductions IA";
  }, []);

  const fetchTranslatedTexts = async () => {
    try {
      setError('');

      if (clients === null) {
        return;
      }

      if (!selectedWebsiteUrl) {
        setTexts([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      if (!currentWebsiteId) {
        setTexts([]);
        setWebsiteId(null);
        setIsLoading(false);
        return;
      }

      setWebsiteId(currentWebsiteId);

      const apiUrl = `/texts/external/unassigned/by-website/${currentWebsiteId}?type=TRANSLATED`;
      const response = await axiosPrivate.get(apiUrl);

      setTexts(response.data || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des textes traduits:', error);
      setError(`Erreur lors du chargement : ${error.response?.data?.message || error.message}`);
      setTexts([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTranslatedTexts();
  }, [selectedWebsiteUrl, currentWebsiteId, clients]);

  useEffect(() => {
    let isMounted = true;
    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        const totalCredits = Number(data?.totalCredits);
        const usedCredits = Number(data?.usedCredits);
        if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
          setRemainingCredits(totalCredits - usedCredits);
        } else {
          const apiRemainingCredits = Number(data?.remainingCredits);
          setRemainingCredits(Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null);
        }
        const rawModels = Array.isArray(data?.availableAiModels) ? data.availableAiModels : [];
        const normalizedModels = rawModels
          .map((model) => {
            const id = normalizeLlmId(
              typeof model === 'string' ? model : (model?.id || model?.value || model?.name || '')
            );
            if (!id) return null;
            return {
              id,
              name: typeof model === 'object' && model?.name ? model.name : formatModelName(id),
            };
          })
          .filter(Boolean);

        const uniqueModels = Array.from(
          new Map(normalizedModels.map((item) => [item.id, item])).values()
        );
        setAvailableAiModels(uniqueModels.length > 0 ? uniqueModels : DEFAULT_LLM_OPTIONS);
      } catch {
        if (isMounted) {
          setAvailableAiModels(DEFAULT_LLM_OPTIONS);
          setRemainingCredits(null);
        }
      }
    };

    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!currentWebsiteId) {
      setRemainingTexts(null);
      return undefined;
    }

    let isMounted = true;

    const fetchWebsiteQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get(`/project/website-quotas/${currentWebsiteId}`);
        if (!isMounted) return;

        const maxTexts = Number(data?.maxTexts);
        const usedTexts = Number(data?.usedTexts);
        if (Number.isFinite(maxTexts) && Number.isFinite(usedTexts)) {
          setRemainingTexts(Math.max(0, maxTexts - usedTexts));
        } else {
          setRemainingTexts(null);
        }
      } catch {
        if (isMounted) {
          setRemainingTexts(null);
        }
      }
    };

    fetchWebsiteQuotas();
    return () => {
      isMounted = false;
    };
  }, [currentWebsiteId]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        if (!selectedWebsiteUrl) {
          setTranslationAgents([]);
          return;
        }
        const response = await axiosPrivate.get(
          `/smart-content/agent_ia/traduction/all?websiteUrl=${encodeURIComponent(
            selectedWebsiteUrl
          )}`
        );
        const agents = (response.data || []).map((agent) => ({
          ...agent,
          displayName: agent.name,
          websiteUrl: selectedWebsiteUrl,
        }));
        setTranslationAgents(agents);
      } catch (e) {
        console.error('Erreur lors du chargement des agents de traduction:', e);
        setTranslationAgents([]);
      }
    };

    fetchAgents();
  }, [selectedWebsiteUrl]);

  const handleGoToAddWebsite = () => {
    history.push('/AdminClient/AjouterSiteWeb');
  };

  const handleCreateTraduction = async () => {
    if (hasNoWebsiteConfigured) {
      return;
    }
    if (remainingCredits !== null && remainingCredits <= 0) {
      setLimitModalOpen(true);
      return;
    }
    setIsTranslationPopinOpen(true);
  };

  const getKeywordColor = (item) => {
    if (!item) return '#666666';
    if (item.deployed === true) return '#8B5CF6'; // violet: en ligne
    if (item.validated === true) return '#10B981'; // vert: validé
    if (item.inRevision === true) return '#C05621'; // orange: en révision
    if (item.delivered === true) return '#000000'; // noir: livré
    return '#666666'; // gris: brouillon
  };

  const handleOptimisticUpdate = (textId, nextStatus) => {
    const statusFlags = {
      Brouillon: { delivered: false, validated: false, deployed: false, inRevision: false },
      Livré: { delivered: true, validated: false, deployed: false, inRevision: false },
      'En révision': { delivered: false, validated: false, deployed: false, inRevision: true },
      Validé: { delivered: true, validated: true, deployed: false, inRevision: false },
      'En ligne': { delivered: true, validated: true, deployed: true, inRevision: false },
    };
    const flags = statusFlags[nextStatus] || statusFlags.Brouillon;
    setTexts((prev) =>
      prev.map((it) =>
        it.id === textId
          ? {
            ...it,
            delivered: flags.delivered,
            validated: flags.validated,
            deployed: flags.deployed,
            inRevision: flags.inRevision,
          }
          : it
      )
    );
  };

  const handleStatusChangeError = (error, previousItem) => {
    if (isTextRevisionNotAllowedError(error)) {
      setIsRevisionErrorModalOpen(true);
    }
    if (!previousItem) return;
    setTexts((prev) =>
      prev.map((it) => (it.id === previousItem.id ? previousItem : it))
    );
  };

  const filteredTexts = useMemo(() => {
    return texts.filter((t) =>
      (t.keyword || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [texts, search]);

  // Pagination
  const { currentPage, setCurrentPage, totalPages, paginatedItems } =
    usePagination(filteredTexts, textsPerPage);

  const rows = paginatedItems;

  const textIdsForExport = useMemo(
    () =>
      (texts || [])
        .map((text) => text?.id || text?.textId)
        .filter(Boolean),
    [texts]
  );

  const handleDownloadAllTexts = useCallback(async () => {
    if (!textIdsForExport.length || isExportingTexts) return;

    let exportModalTimeout;
    setIsExportingTexts(true);
    try {
      exportModalTimeout = setTimeout(() => setShowExportModal(true), 500);
      await exportTextsByIdsAsZip(textIdsForExport, {
        fileName: 'Export traductions - Typix.zip',
      });
    } catch (error) {
      console.error('Erreur lors du téléchargement des traductions:', error);
      setExportErrorMessage(t("Une erreur est survenue lors de l'exportation."));
      setExportErrorOpen(true);
    } finally {
      if (exportModalTimeout) clearTimeout(exportModalTimeout);
      setShowExportModal(false);
      setIsExportingTexts(false);
    }
  }, [textIdsForExport, isExportingTexts, t]);

  const { handleStatusChange: handleTextStatusChange, isUpdating } =
    useTextStatusChange(
      null,
      handleStatusChangeError,
      handleOptimisticUpdate
    );

  const getStatusLabel = (item) => {
    if (!item) return t('Brouillon');
    if (item.deployed === true) return t('En ligne');
    if (item.validated === true) return t('Validé');
    if (item.inRevision === true) return t('En révision');
    if (item.delivered === true) return t('Livré');
    return t('Brouillon');
  };

  const getStatusIconSrc = (item) => {
    if (!item) return '/Images/drafted-text-icon.png';
    if (item.deployed === true) return '/Images/deployed-text-icon.png';
    if (item.validated === true) return '/Images/validate-text-icon.png';
    if (item.inRevision === true) return '/Images/en_revision.png';
    if (item.delivered === true) return '/Images/delivred-text-icon.png';
    return '/Images/drafted-text-icon.png';
  };

  const handleRowClick = (row) => {
    if (!row || !row.id) return;
    localStorage.setItem('AdminClient_TextId', row.id);
    history.push(`${createTranslationIaPath}/${row.id}`);
  };

  const columns = [
    {
      header: t('Mot clé'),
      accessor: 'keyword',
      sortable: false,
      width: '61%',
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              color: getKeywordColor(row),
              fontSize: 14,
              fontWeight: 600,
            }}
            title={getStatusLabel(row)}
          >
            {row.keyword || 'N/A'}
          </span>
          {row.url && (
            <span
              title={row.url}
              style={{
                fontSize: 12,
                color: '#1A1A1A',
                display: 'block',
                wordBreak: 'break-all',
              }}
            >
              {row.url}
            </span>
          )}
        </div>
      ),
    },
    {
      header: t('Date de création'),
      accessor: 'createdAt',
      sortable: false,
      width: '15%',
      renderCell: (row) =>
        row.createdAt ? formatDateShort(row.createdAt) : '-',
    },
    {
      header: t('Date de livraison'),
      accessor: 'deliveredAt',
      sortable: false,
      width: '15%',
      renderCell: (row) =>
        row.deliveredAt ? formatDateShort(row.deliveredAt) : '-',
    },
    {
      header: t('Status'),
      accessor: 'status',
      sortable: false,
      width: '20%',
      renderCell: (row) => {
        if (isConsultantRoute) {
          return (
            <div
              style={{
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                position: 'relative',
              }}
            >
              <img
                src={getStatusIconSrc(row)}
                alt={getStatusLabel(row)}
                style={{ width: 16, height: 16 }}
              />
              <span>{getStatusLabel(row)}</span>
            </div>
          );
        }

        const isOpen = openStatusId === row.id;
        const previousItem = { ...row };
        const statusOptions = ['Brouillon', 'Livré', 'En révision', 'Validé', 'En ligne'];

        return (
          <div
            style={{
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              position: 'relative',
            }}
          >
            <img
              src={getStatusIconSrc(row)}
              alt={getStatusLabel(row)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();

                if (openStatusId === row.id) {
                  setOpenStatusId(null);
                  return;
                }

                const rect = e.currentTarget.getBoundingClientRect();
                const estimatedHeight = statusOptions.length * 36 + 12;
                const menuWidth = 160;
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

                let top = rect.bottom + 4;
                if (top + estimatedHeight > viewportHeight) {
                  top = rect.top - estimatedHeight - 4;
                }

                let left = rect.right - menuWidth;
                if (left + menuWidth > viewportWidth - 8) {
                  left = viewportWidth - menuWidth - 8;
                }
                if (left < 8) left = 8;

                setStatusMenuCoords({ top, left });
                setOpenStatusId(row.id);
              }}
            />
            <span>{getStatusLabel(row)}</span>
            <img
              src="/Images/status-icon.png"
              alt="status icon"
              style={{ width: 16, height: 16, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();

                if (openStatusId === row.id) {
                  setOpenStatusId(null);
                  return;
                }

                const rect = e.currentTarget.getBoundingClientRect();
                const estimatedHeight = statusOptions.length * 36 + 12;
                const menuWidth = 160;
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

                let top = rect.bottom + 4;
                if (top + estimatedHeight > viewportHeight) {
                  top = rect.top - estimatedHeight - 4;
                }

                let left = rect.right - menuWidth;
                if (left + menuWidth > viewportWidth - 8) {
                  left = viewportWidth - menuWidth - 8;
                }
                if (left < 8) left = 8;

                setStatusMenuCoords({ top, left });
                setOpenStatusId(row.id);
              }}
            />
            {isOpen && (
              <div
                style={{
                  position: 'fixed',
                  top: statusMenuCoords.top,
                  left: statusMenuCoords.left,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                  borderRadius: 8,
                  padding: '6px 0',
                  minWidth: 160,
                  zIndex: 1000,
                }}
              >
                {statusOptions.map((status) => {
                  const isActive = getStatusLabel(row) === t(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        fontSize: 12,
                        color: isActive ? '#111827' : '#4B5563',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        backgroundColor: isActive ? '#F1F1F1' : 'transparent',
                        border: 'none',
                        fontWeight: isActive ? 600 : 400,
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setOpenStatusId(null);
                        if (!isActive && row.id) {
                          await handleTextStatusChange(
                            row.id,
                            status,
                            previousItem
                          );
                        }
                      }}
                    >
                      <img
                        src={getStatusIconSrc({
                          ...row,
                          deployed: status === 'En ligne',
                          validated: status === 'Validé',
                          inRevision: status === 'En révision',
                          delivered:
                            status === 'Livré' ||
                            status === 'Validé' ||
                            status === 'En ligne',
                        })}
                        alt={status}
                        style={{ width: 14, height: 14 }}
                      />
                      <span>{t(status)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: t('Actions'),
      accessor: 'actions',
      sortable: false,
      width: '15%',
      renderCell: (row) => (
        <div
          className="row-actions"
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="btn action-btn"
            onClick={(e) => {
              e.stopPropagation();
              localStorage.setItem('AdminClient_TextId', row.id);
              history.push(`${createTranslationIaPath}/${row.id}`);
            }}
            title={t('Modifier')}
          >
            <img
              src="/Images/edit-icon.png"
              alt={t('Modifier')}
              width={16}
              height={16}
            />
          </button>
          <button
            className="btn action-btn"
            onClick={(e) => {
              e.stopPropagation();

              if (openActionsId === row.id) {
                setOpenActionsId(null);
                return;
              }

              const rect = e.currentTarget.getBoundingClientRect();
              const estimatedHeight = 120;
              const menuWidth = 190;
              const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
              const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

              let top = rect.bottom + 4;
              if (top + estimatedHeight > viewportHeight) {
                top = rect.top - estimatedHeight - 4;
              }

              let left = rect.left;
              if (left + menuWidth > viewportWidth - 8) {
                left = viewportWidth - menuWidth - 8;
              }
              if (left < 8) left = 8;

              setActionsMenuCoords({ top, left });
              setOpenActionsId(row.id);
            }}
            title={t('Plus d’actions')}
          >
            <img
              src="/Images/plus-icon.png"
              alt="plus"
              width={16}
              height={16}
            />
          </button>

          {openActionsId === row.id && (
            <div
              style={{
                position: 'fixed',
                top: actionsMenuCoords.top,
                left: actionsMenuCoords.left,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
                borderRadius: 10,
                padding: '8px 0',
                minWidth: 180,
                zIndex: 1000,
              }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  padding: '6px 14px',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  fontSize: 13,
                  color: '#111827',
                  cursor: 'pointer',
                  display: 'block',
                }}
                onClick={async () => {
                  try {
                    await DownloadTextWordType(row.id);
                  } catch (error) {
                    console.error('Erreur lors du téléchargement:', error);
                  } finally {
                    setOpenActionsId(null);
                  }
                }}
              >
                {t('Telecharger le texte')}
              </button>

              <div
                style={{
                  borderTop: '1px solid #F3F4F6',
                  margin: '4px 0',
                }}
              />

              <button
                type="button"
                style={{
                  width: '90%',
                  padding: '6px 14px',
                  textAlign: 'left',
                  background: '#F1F1F1',
                  border: 'none',
                  fontSize: 13,
                  color: '#B91C1C',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 6,
                  marginLeft: '5%',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setTextPendingDelete(row);
                  setDeleteModalOpen(true);
                  setOpenActionsId(null);
                }}
              >
                <img
                  src="/Images/icon-trash.png"
                  alt={t('Supprimer')}
                  style={{ width: 18, height: 18 }}
                />
                <span>{t('Supprimer')}</span>
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];
  const displayedColumns = isConsultantRoute
    ? columns.filter((column) => column.accessor !== 'actions')
    : columns;

  return (
    <div className="content-center">
      <style>{`
          .url-icon-container {
            position: relative;
            display: inline-flex;
            align-items: center;
            color: #6b7280;
            cursor: pointer;
          }

          .url-tooltip {
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background-color: #6b7280;
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            white-space: normal;
            max-width: 420px;
            overflow-wrap: break-word;
            word-break: normal;
            z-index: 1000;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.2s ease, visibility 0.2s ease;
            margin-bottom: 8px;
            box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
          }

          .url-tooltip::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-top-color: #6b7280;
          }

          .url-icon-container:hover .url-tooltip {
            opacity: 1;
            visibility: visible;
          }
        `}</style>
      <div className="box-info-header">
        <div className="b-child-1">
          <h1 >{t('Traduction IA')}</h1>
        </div>
        <div className="b-child-2 d-flex justify-content-end align-items-center ai-smart-header-actions">
          {!isConsultantRoute && (
            <>
              <div className="ai-smart-stats-panel" aria-label={t('Quotas disponibles')}>
                <div className="ai-smart-stat-block">
                  <div className="ai-smart-icon-wrapper ai-smart-icon-yellow" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                      <path d="M20 3v4" />
                      <path d="M22 5h-4" />
                      <path d="M4 17v2" />
                      <path d="M5 18H3" />
                    </svg>
                  </div>
                  <div className="ai-smart-stat-text">
                    <span className="ai-smart-stat-label">{t('Crédits IA')}</span>
                    <span className="ai-smart-stat-value">{formatQuotaDisplay(remainingCredits)}</span>
                  </div>
                </div>
                <div className="ai-smart-stat-divider" aria-hidden="true" />
                <div className="ai-smart-stat-block">
                  <div className="ai-smart-icon-wrapper ai-smart-icon-purple" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" x2="8" y1="13" y2="13" />
                      <line x1="16" x2="8" y1="17" y2="17" />
                      <line x1="10" x2="8" y1="9" y2="9" />
                    </svg>
                  </div>
                  <div className="ai-smart-stat-text">
                    <span className="ai-smart-stat-label">{t('Textes restants')}</span>
                    <span className="ai-smart-stat-value">{formatQuotaDisplay(remainingTexts)}</span>
                  </div>
                </div>
              </div>
              <button
                className="ai-smart-btn-create"
                type="button"
                onClick={handleCreateTraduction}
                disabled={hasNoWebsiteConfigured}
                aria-disabled={hasNoWebsiteConfigured}
              >
                + {t('Nouveau contenu')}
              </button>
            </>
          )}
        </div>
      </div>

      <hr
        style={{
          border: 'none',
          borderBottom: '1px solid rgb(179, 180, 182)',
          marginLeft: '-30px',
          marginBottom: 8,
          width: 'calc(100% + 40px)',
        }}
      />

      {hasNoWebsiteConfigured && (
        <div className="translation-ia-help-banner" role="note" style={{ marginBottom: 12 }}>
          <div className="translation-ia-help-banner-left">
            <span className="translation-ia-help-banner-text">
              {t('Aucun contenu disponible — veuillez d’abord ajouter un siteweb.')}
            </span>
          </div>
          {!isConsultantRoute && (
            <div className="translation-ia-help-banner-actions">
              <button
                type="button"
                onClick={handleGoToAddWebsite}
                className="translation-ia-help-banner-link"
              >
                {t('Ajouter un siteweb')}
              </button>
            </div>
          )}
        </div>
      )}

      {websiteId && (
        <div
          style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {!isSearchOpen && textIdsForExport.length > 0 && (
            <button
              type="button"
              onClick={handleDownloadAllTexts}
              disabled={isExportingTexts}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid #E5E7EB',
                backgroundColor: '#FFFFFF',
                fontSize: 13,
                fontWeight: 500,
                color: '#111827',
                cursor: isExportingTexts ? 'not-allowed' : 'pointer',
                opacity: isExportingTexts ? 0.7 : 1,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              <img
                src="/Images/icon-download-simple.png"
                alt="download texts"
                style={{ width: 14, height: 14 }}
              />
              <span>{t('Téléchargez tous les textes.')}</span>
            </button>
          )}

          {!isSearchOpen ? (
            <img
              src="/Images/search-icon.png"
              alt="search"
              onClick={() => setIsSearchOpen(true)}
              style={{
                width: '32px',
                height: '32px',
                cursor: 'pointer',
              }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                maxWidth: '320px',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <img
                  src="/Images/search-icon2.png"
                  alt="search"
                  style={{
                    position: 'absolute',
                    left: '10px',
                    width: '16px',
                    height: '16px',
                    pointerEvents: 'none',
                  }}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('Rechercher un texte...')}
                  style={{
                    width: '100%',
                    padding: '8px 36px 8px 34px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearch('');
                }}
                style={{
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#111827',
                }}
              >
                {t('Fermer')}
              </button>
            </div>
          )}
        </div>
      )}

      <div>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <DataTable
          headerClassName="ai-smart-table-head"
          columns={displayedColumns}
          data={rows}
          enableSorting={false}
          onRowClick={handleRowClick}
          emptyStateContent={
            !isLoading && !error ? (
              <div style={{ padding: '12px 16px', textAlign: 'center', color: '#6B7280' }}>
                {t('Aucune traduction trouvée')}
              </div>
            ) : null
          }
        />
      </div>
      {!isLoading && rows.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disablePrev={currentPage === 1}
          disableNext={currentPage === totalPages || rows.length === 0}
        />
      )}
      <TranslationPopin
        isOpen={isTranslationPopinOpen}
        onClose={() => setIsTranslationPopinOpen(false)}
        agents={translationAgents}
        llmOptions={availableAiModels}
        disableGenerateButton={
          hasNoWebsiteConfigured ||
          (remainingCredits !== null && remainingCredits <= 0)
        }
        onBlockedGenerateClick={() => {
          setIsTranslationPopinOpen(false);
          if (hasNoWebsiteConfigured) {
            history.push('/AdminClient/AjouterSiteWeb');
            return;
          }
          setLimitModalOpen(true);
        }}
        onSubmit={(formValues) => {
          if (hasNoWebsiteConfigured) {
            setIsTranslationPopinOpen(false);
            return;
          }
          try {
            const context = {
              form: {
                keyword: formValues.keyword || '',
                targetUrl: formValues.targetUrl || '',
                agentId: formValues.agentId || '',
                llm: formValues.llm || '',
              },
              meta: {
                websiteUrl: selectedWebsiteUrl,
              },
            };
            window.localStorage.setItem(
              TRANSLATION_POPIN_CONTEXT_KEY,
              JSON.stringify(context)
            );
          } catch (e) {
            // ignore storage errors
          }
          history.push(createTranslationIaPath);
          setIsTranslationPopinOpen(false);
        }}
      />
      <ProjectCreatedModal
        open={limitModalOpen}
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onConfirm={() => {
          setLimitModalOpen(false);
          setIsBuyCreditsModalOpen(true);
        }}
        title={t('Limite atteinte')}
        message={t(
          'Vous avez atteint la limite autorisée par votre forfait.\n\nRechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.'
        )}
        confirmButtonText={t('Acheter des crédits')}
        secondaryButtonText={t('Passer à une offre supérieure')}
        onSecondaryAction={() => history.push('/AdminClient/Forfait')}
      />
      <BuyCreditsModal
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
      />
      <DownloadModal
        open={showExportModal}
        handleClose={() => setShowExportModal(false)}
      />
      <ErrorSuccesModal
        open={exportErrorOpen}
        message={exportErrorMessage}
        handleClose={() => setExportErrorOpen(false)}
        success={false}
        toggle={() => setExportErrorOpen((prev) => !prev)}
      />
      <QuitProjectCreationModal
        isOpen={isRevisionErrorModalOpen}
        onClose={() => setIsRevisionErrorModalOpen(false)}
        variant="enrichment"
        title={t('Mise en révision impossible')}
        message={t(
          'Ce texte est validé ou mis en ligne. Il ne peut pas être envoyé en révision.'
        )}
        primaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => setIsRevisionErrorModalOpen(false)}
        showSecondaryButton={false}
        showSuggestion={false}
      />
      <ConfirmationModal
        open={deleteModalOpen}
        message={t('Êtes-vous sûr de vouloir supprimer ce contenu ?')}
        handleClose={() => {
          setDeleteModalOpen(false);
          setTextPendingDelete(null);
        }}
        executeAction={async () => {
          if (!textPendingDelete || !textPendingDelete.id) {
            setDeleteModalOpen(false);
            setTextPendingDelete(null);
            return;
          }
          try {
            await axiosPrivate.delete(`/texts/external/unassigned/${textPendingDelete.id}/delete`);
            const deletedTextId = String(textPendingDelete.id);
            setTexts((prev) => prev.filter((txt) => String(txt?.id) !== deletedTextId));
          } catch (error) {
            console.error('Erreur lors de la suppression du texte traduit:', error);
          } finally {
            setDeleteModalOpen(false);
            setTextPendingDelete(null);
          }
        }}
      />
    </div>
  );
};

export default TraductionIaList;
